import React, { useEffect, useMemo } from 'react';
import { useBreakpoint } from 'hooks';
import { Title, Icon, Text, Tooltip, Button, Alert } from 'components';
import { FeedbackTemplate } from '@bb/domain/dist/types/Feedback';
import {
  TemplateRoles,
  TemplateUsage,
  TEMPLATE_ROLE_PRIORITY_MAP,
} from '@bb/domain/dist/types';
import { cloneDeep } from 'lodash';
import TemplateDropdown, {
  TemplateDropdownType,
} from '@app/template/TemplateDropdown';
import styled from 'styled-components';
import { defaultTemplates, sortTemplates } from 'utils';
import FormBuilder from './FormBuilder';

export type FeedbackSurveyProps = {
  template: FeedbackTemplate[];
  surveyTemplates: TemplateDropdownType;
  updateTemplate: (tempalte: FeedbackTemplate[]) => void;
  setSurveyTemplateId: (templateId: string) => void;
  surveyTemplateId: string;
  setIsCustomSelected: (isCustomSelected: boolean) => void;
  setIsInvalidSurvey: (isInvalid: boolean) => void;
};

const FeedbackSurvey: React.FC<FeedbackSurveyProps> = ({
  template,
  updateTemplate,
  surveyTemplates,
  setSurveyTemplateId,
  surveyTemplateId,
  setIsCustomSelected,
  setIsInvalidSurvey,
}) => {
  const { lg, xl } = useBreakpoint();

  const updateTemplateWrapper = (templates: FeedbackTemplate[]) => {
    updateTemplate(templates);
    setIsInvalidSurvey(!templates || templates.length === 0);
  };

  const processedTemplates = useMemo(() => {
    let processed = cloneDeep(surveyTemplates || []);
    // Add a root default template if necessary
    if (!processed.find(t => t.isDefault)) {
      processed.push({
        ...defaultTemplates.feedbackSurvey,
        id: 'default',
        name: 'Default Feedback Survey Template',
        type: 'feedbackSurvey',
        isDefault: true,
        role: 'GLOBAL',
        updatedAt: undefined,
      });
    }

    // 1.user default 2.team default 3.branch default 4.global default, 5. last edited
    processed = sortTemplates(processed);
    // Add a custom template selection
    const custom = {
      subject: '',
      body: JSON.stringify(template),
      id: 'custom',
      name: 'Custom',
      type: 'feedbackSurvey' as TemplateUsage,
      role: 'USER' as TemplateRoles,
    };
    processed.push(custom);

    return processed;
  }, [surveyTemplates]);

  useEffect(() => {
    if (!processedTemplates.length || surveyTemplateId) return;
    const primaryDefaultId = processedTemplates
      .filter(t => t.isDefault)
      .sort(
        (a, b) =>
          TEMPLATE_ROLE_PRIORITY_MAP[a.role] -
          TEMPLATE_ROLE_PRIORITY_MAP[b.role],
      )[0].id;
    setSurveyTemplateId(primaryDefaultId);
  }, [processedTemplates]);

  // uptate template when survey template id changes
  useEffect(() => {
    if (!surveyTemplateId) return;
    const selectedTemplate = processedTemplates.find(
      t => t.id === surveyTemplateId,
    );
    if (!selectedTemplate) return;
    updateTemplate(JSON.parse(selectedTemplate.body));
    setIsCustomSelected(surveyTemplateId === 'custom');
  }, [surveyTemplateId, processedTemplates, updateTemplate]);

  return (
    <div>
      <div>
        <Title level={3} data-cy="feedback-survey-title">
          Feedback Survey Template
          <Tooltip
            overlayStyle={lg ? { maxWidth: '550px' } : undefined}
            placement="right"
            title={
              <Text color="white" data-cy="feedback-survey-info-text">
                This page gives you the ability to assign an existing survey
                template or create a custom survey for this listing. Create a
                custom template by clicking on the{' '}
                <Text strong color="white">
                  Use Template
                </Text>{' '}
                dropdown and scroll to the bottom!
              </Text>
            }
          >
            <Text>
              <Icon
                type="antdInfoCircleFilled"
                color="gray8"
                offset={[5, -5]}
                data-cy="feedback-survey-info-icon"
              />
            </Text>
          </Tooltip>
        </Title>
      </div>
      <label>Use Template</label>
      <HeaderContainer $xl={xl}>
        <DropdownContainer>
          <TemplateDropdown
            templates={processedTemplates}
            value={surveyTemplateId}
            onChange={setSurveyTemplateId}
          />
          <a href="/#/template/manage">
            <Button type="default" strong data-cy="manage-templates-button">
              Manage Templates
            </Button>
          </a>
        </DropdownContainer>
        <Alert
          message={
            surveyTemplateId !== 'custom' ? (
              <>
                This is an existing template and cannot be edited. To edit
                templates, click on the <strong>Manage Templates</strong> button
                to be redirected to the Template Manager.
              </>
            ) : (
              'The feedback template below will be delivered to showing agents following their showing, asking them to provide feedback.'
            )
          }
          type={surveyTemplateId !== 'custom' ? 'warning' : 'info'}
          showIcon
        />
      </HeaderContainer>
      <FormBuilder
        template={template}
        updateTemplate={updateTemplateWrapper}
        isPreview={surveyTemplateId !== 'custom'}
        key={surveyTemplateId}
        notEditable
      />
    </div>
  );
};

export default FeedbackSurvey;

const HeaderContainer = styled.div<{ $xl: boolean }>`
  max-width: ${props => (props.$xl ? '50%' : '100%')};
  margin-bottom: 30px;
`;

const DropdownContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 14px;
  & > div {
    width: 100%;
  }
`;
